import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LegalLinks from '@/components/LegalLinks';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import {
  AppButton,
  AppCard,
  AppCardBody,
  AppCardHeader,
  AppContentContainer,
  AppFieldGroup,
  AppInput,
  AppPageIntro,
  AppSectionHeader,
  AppShell,
} from '../../ui-foundation';

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let mounted = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setReady(Boolean(data.session));
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;
      if (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        setReady(Boolean(session));
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!ready) {
      toast.error('Reset link is missing or has expired.');
      return;
    }

    if (password.length < 8) {
      toast.error('Password must be at least 8 characters.');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match.');
      return;
    }

    setSaving(true);
    const { error } = await supabase.auth.updateUser({ password });
    setSaving(false);

    if (error) {
      toast.error('Could not update password.');
      return;
    }

    toast.success('Password updated. You can sign in now.');
    await supabase.auth.signOut();
    navigate('/auth', { replace: true });
  };

  return (
    <AppShell className="min-h-screen justify-center px-4 py-6 sm:py-10">
      <AppContentContainer maxWidth={540} className="w-full animate-fade-in">
        <div className="rounded-[32px] border border-border bg-card/95 p-4 shadow-sm sm:p-5">
          <div className="rounded-[28px] border border-primary/10 bg-[linear-gradient(180deg,rgba(107,53,102,0.08),rgba(255,255,255,0.92))] px-5 py-6 sm:px-7 sm:py-7">
            <AppPageIntro
              title="Reset password"
              subtitle="Choose a new password for your Adulting account."
            />
          </div>

          <div className="mt-4">
            <AppCard>
              <AppCardHeader>
                <AppSectionHeader
                  title={ready ? 'Create a new password' : 'Open your reset email first'}
                  subtitle={
                    ready
                      ? 'Use at least 8 characters and confirm it once before saving.'
                      : 'If the reset link has expired, request a fresh one from Settings or the sign-in page.'
                  }
                />
              </AppCardHeader>
              <AppCardBody>
                {!ready ? (
                  <div className="space-y-4">
                    <p className="text-sm leading-relaxed text-muted-foreground">
                      Open this page directly from your password reset email. Adulting needs that
                      recovery session before it can safely save a new password.
                    </p>
                    <AppButton size="sm" onPress={() => navigate('/auth', { replace: true })}>
                      Back to sign in
                    </AppButton>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <AppFieldGroup label="New password" helper="Use at least 8 characters.">
                      <AppInput
                        type="password"
                        value={password}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                        autoComplete="new-password"
                      />
                    </AppFieldGroup>

                    <AppFieldGroup label="Confirm password">
                      <AppInput
                        type="password"
                        value={confirmPassword}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value)}
                        autoComplete="new-password"
                      />
                    </AppFieldGroup>

                    <AppButton size="sm" type="submit" disabled={saving}>
                      {saving ? 'Updating...' : 'Update password'}
                    </AppButton>
                  </form>
                )}
              </AppCardBody>
            </AppCard>

            <div className="mt-4 rounded-[24px] border border-border bg-card/80 px-5 py-4 shadow-sm">
              <LegalLinks />
            </div>
          </div>
        </div>
      </AppContentContainer>
    </AppShell>
  );
}
